import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  merge,
  Observable,
  startWith,
  switchMap,
  from,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import { ResultListComponent } from './ui/result-list/result-list.component';
import { SearchInputComponent } from './ui/search-input/search-input.component';
import { GoogleMapsService } from '@app/services/google-maps.service';
import { AddressDetails, PlaceSuggestion } from '@app/interfaces/google-maps.interface';
import { SearchState } from '@app/interfaces/places-autocomplete.interface';
import { NewRelicEventsService } from '@app/services/new-relic-events.service';
import { ADDRESS_STATUS, NEW_RELIC_EVENT } from '@app/enums';
import { PlacesError, classifyPlacesInitError } from '@app/shared/components/places-autocomplete/utils/helpers/places-error';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

@Component({
  selector: 'am-places-autocomplete',
  templateUrl: './places-autocomplete.component.html',
  styleUrls: ['./places-autocomplete.component.scss'],
  standalone: false,
})
export class PlacesAutocompleteComponent implements OnInit, OnDestroy {
  @Input() initialSearch = '';
  @Output() placeSelected = new EventEmitter<AddressDetails>();
  @Output() closeSearch = new EventEmitter<void>();
  @Output() searchError = new EventEmitter<SearchState>();
  @ViewChild('searchInput') searchInput!: SearchInputComponent;

  searchControl = new FormControl('');
  searchState$: Observable<SearchState>;
  canScrollUp = false;
  canScrollDown = false;

  private scrollContainer: HTMLElement | null = null;
  private destroy$ = new Subject<void>();
  private placeError$ = new Subject<SearchState>();

  constructor(
    private googleService: GoogleMapsService,
    private newRelicEventsService: NewRelicEventsService,
    private cdr: ChangeDetectorRef
  ) {}

  @ViewChild('resultsList') set resultsListRef(ref: ResultListComponent | null) {
    this.cleanupScrollListener();

    if (ref?.listContainer) {
      this.scrollContainer = ref.listContainer.nativeElement;
      this.scrollContainer.addEventListener('scroll', this.scrollHandler);
      setTimeout(() => this.checkScroll(), 0);
    }
  }

  ngOnInit() {
    if (this.initialSearch) {
      this.searchControl.setValue(this.initialSearch, { emitEvent: false });
    }

    const eventName = this.initialSearch
      ? NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_RESEARCH_INIT
      : NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_INIT;

    try {
      if (!navigator.onLine) {
        throw new Error('Network unavailable');
      }

      const google = (window as any).google;
      if (!google?.maps) {
        throw new Error('Google Maps SDK not defined');
      }

      this.newRelicEventsService.trackEvent(eventName, {
        isError: false,
        isService: false,
        status: ADDRESS_STATUS.SUCCESS,
      });
    } catch (err) {
      const errorType = classifyPlacesInitError(err);
      this.newRelicEventsService.trackEvent(eventName, {
        isError: true,
        isService: false,
        status: ADDRESS_STATUS.FAILURE,
        error_type: errorType,
        error_message: (err as Error)?.message ?? String(err),
      });
    }

    this.searchState$ = this.setupSearchStream();
  }

  ngOnDestroy() {
    this.cleanupScrollListener();
    this.placeError$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  async onPlaceSelected(place: PlaceSuggestion): Promise<void> {
    this.searchControl.setValue(place.mainText, { emitEvent: false });

    try {
      const details = await this.googleService.getPlaceDetails(place.placeId);
      const dataToSendNewRelic = {
        isError: false,
        isService: true,
        status: ADDRESS_STATUS.SUCCESS,
      };

      this.newRelicEventsService.trackEvent(
        NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_SELECTION,
        dataToSendNewRelic
      );
      this.placeSelected.emit(details);
    } catch (err) {
      const placesError = err as PlacesError;
      const dataToSendNewRelic = {
        status: ADDRESS_STATUS.FAILURE,
        isError: true,
        error_type: placesError.type,
        error_message: placesError.original,
      };

      this.newRelicEventsService.trackEvent(
        NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_SELECTION,
        dataToSendNewRelic
      );

      this.placeError$.next(this.sendSearchStateError());
    }
  }

  private sendSearchStateError(): SearchState {
    const searchState: SearchState = { results: [], hasSearched: true, error: true };
    this.searchError.emit(searchState);
    return searchState;
  }

  private setupSearchStream(): Observable<SearchState> {
    const search$ = this.searchControl.valueChanges.pipe(
      startWith(this.searchControl.value),
      debounceTime(DEBOUNCE_MS),
      distinctUntilChanged(),
      switchMap((searchText) => {
        if (!searchText || searchText.length < MIN_SEARCH_LENGTH) {
          return of<SearchState>({ results: [], hasSearched: false, error: false });
        }

        return from(this.googleService.getAutocompleteSuggestions(searchText)).pipe(
          switchMap((results) => {
            setTimeout(() => this.checkScroll(), 0);
            return of<SearchState>({ results, hasSearched: true, error: false });
          }),
          catchError((err) => {
            const placesError = err as PlacesError;
            const dataToSendNewRelic = {
              isError: true,
              isService: true,
              status: ADDRESS_STATUS.FAILURE,
              error_type: placesError.type,
              error_message: placesError.original,
            };
            this.newRelicEventsService.trackEvent(
              NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_SEARCH,
              dataToSendNewRelic
            );
            return of(this.sendSearchStateError());
          })
        );
      })
    );

    return merge(search$, this.placeError$).pipe(takeUntil(this.destroy$));
  }

  private checkScroll() {
    if (!this.scrollContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = this.scrollContainer;
    const up = scrollTop > 0;
    const down = scrollTop + clientHeight < scrollHeight - 1;

    if (this.canScrollUp !== up || this.canScrollDown !== down) {
      this.canScrollUp = up;
      this.canScrollDown = down;
      this.cdr.detectChanges();
    }
  }

  private cleanupScrollListener() {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
      this.scrollContainer = null;
    }
  }

  private readonly scrollHandler = () => this.checkScroll();
}
